import React, { useEffect, useState } from 'react';
import { updateMyAccount } from '../utils/api';
import { ar, en } from '../utils/translations';
import { COUNTRY_CODES, splitPhoneAndCode } from '../utils/countryCodes';

const colors = {
  primary: '#f97316',
  primaryDark: '#ea580c',
  background: '#fffef8',
  card: '#fffaf5',
  border: '#ffecc8',
  text: '#1a1a1a',
  textMuted: '#6a6a6a'
};

const AccountInfo = ({ language = 'ar', user, onUserUpdate, onClose }) => {
  const t = language === 'ar' ? ar : en;
  const isRTL = language === 'ar';
  const [account, setAccount] = useState(user || null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({ name: '', phone_number: '' });
  const [phoneCountryCode, setPhoneCountryCode] = useState('+966');
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });

  useEffect(() => {
    setAccount(user || null);
    const { code, local } = splitPhoneAndCode(user?.phone_number);
    setPhoneCountryCode(code);
    setFormData({
      name: user?.name || '',
      phone_number: local
    });
    setIsEditing(false);
  }, [user]);

  const formatDate = (value) => {
    if (!value) return t.notAvailable || (language === 'ar' ? 'غير متوفر' : 'Not available');
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US');
  };

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const localDigits = String(formData.phone_number || '').replace(/\D/g, '').replace(/^0+/, '');
      const payload = {
        name: formData.name,
        phone_number: localDigits ? phoneCountryCode.replace(/\D/g, '') + localDigits : ''
      };
      const wantPw = passwordForm.next && passwordForm.next.trim();
      if (wantPw) {
        if (passwordForm.next.length < 6) {
          setError(language === 'ar' ? 'كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل' : 'New password must be at least 6 characters');
          setSaving(false);
          return;
        }
        if (passwordForm.next !== passwordForm.confirm) {
          setError(language === 'ar' ? 'تأكيد كلمة المرور غير متطابق' : 'Password confirmation does not match');
          setSaving(false);
          return;
        }
        if (!passwordForm.current) {
          setError(language === 'ar' ? 'أدخل كلمة المرور الحالية' : 'Enter your current password');
          setSaving(false);
          return;
        }
        payload.current_password = passwordForm.current;
        payload.new_password = passwordForm.next;
      }
      const response = await updateMyAccount(payload);
      if (response.data?.user) {
        setAccount(response.data.user);
        const { code: savedCode, local: savedLocal } = splitPhoneAndCode(response.data.user.phone_number);
        setPhoneCountryCode(savedCode);
        setFormData({
          name: response.data.user.name || '',
          phone_number: savedLocal
        });
        if (onUserUpdate) {
          onUserUpdate(response.data.user);
        }
        setSuccess(t.accountUpdated || (language === 'ar' ? 'تم تحديث الحساب بنجاح' : 'Account updated successfully'));
        setIsEditing(false);
        setPasswordForm({ current: '', next: '', confirm: '' });
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          (t.failedToSaveAccount || (language === 'ar' ? 'فشل حفظ معلومات الحساب.' : 'Failed to save account info.'))
      );
    } finally {
      setSaving(false);
    }
  };

  /** Password-only update (uses saved name/phone; no need to open profile edit). */
  const handlePasswordSave = async () => {
    if (!account) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (!passwordForm.next || !passwordForm.next.trim()) {
        setError(language === 'ar' ? 'أدخل كلمة المرور الجديدة' : 'Enter a new password');
        setSaving(false);
        return;
      }
      if (passwordForm.next.length < 6) {
        setError(language === 'ar' ? 'كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل' : 'New password must be at least 6 characters');
        setSaving(false);
        return;
      }
      if (passwordForm.next !== passwordForm.confirm) {
        setError(language === 'ar' ? 'تأكيد كلمة المرور غير متطابق' : 'Password confirmation does not match');
        setSaving(false);
        return;
      }
      if (!passwordForm.current) {
        setError(language === 'ar' ? 'أدخل كلمة المرور الحالية' : 'Enter your current password');
        setSaving(false);
        return;
      }
      const response = await updateMyAccount({
        name: account.name,
        phone_number: account.phone_number,
        current_password: passwordForm.current,
        new_password: passwordForm.next,
      });
      if (response.data?.user) {
        setAccount(response.data.user);
        if (onUserUpdate) onUserUpdate(response.data.user);
        setSuccess(
          language === 'ar' ? 'تم تحديث كلمة المرور بنجاح' : 'Password updated successfully'
        );
        setPasswordForm({ current: '', next: '', confirm: '' });
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          (language === 'ar' ? 'فشل تحديث كلمة المرور.' : 'Failed to update password.')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: account?.name || '',
      phone_number: account?.phone_number || ''
    });
    setPasswordForm({ current: '', next: '', confirm: '' });
    setError('');
    setSuccess('');
    setIsEditing(false);
  };

  const labelStyle = {
    color: colors.textMuted,
    fontSize: '0.9rem',
    minWidth: '140px'
  };

  const valueStyle = {
    color: colors.text,
    fontWeight: 500
  };

  const getRoleLabel = (role) => {
    if (!role) return '-';
    if (language === 'ar') {
      if (role === 'owner') return 'مالك';
      if (role === 'manager') return 'مدير';
      if (role === 'employee') return 'موظف';
    }
    return role;
  };

  return (
    <div style={{ padding: '20px 0' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ color: colors.text, marginBottom: '6px' }}>{t.accountInfo || (language === 'ar' ? 'معلومات الحساب' : 'Account Info')}</h2>
          <p style={{ color: colors.textMuted, marginBottom: '12px' }}>
            {language === 'ar' ? 'عرض تفاصيل الحساب الأساسية.' : 'View your core account details.'}
          </p>
        </div>
        {onClose && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={onClose}
              style={{
                background: '#fff',
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                padding: '10px 18px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              {t.back || (language === 'ar' ? 'رجوع' : 'Back')}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div style={{ background: '#fff4e6', border: `1px solid ${colors.border}`, color: colors.primaryDark, padding: '10px 12px', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ background: '#fff7ed', border: `1px solid ${colors.border}`, color: colors.primaryDark, padding: '10px 12px', borderRadius: '8px', marginBottom: '16px' }}>
          {success}
        </div>
      )}

      {!account && !loading && (
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, padding: '16px', borderRadius: '12px', color: colors.textMuted }}>
          {t.noUser || (language === 'ar' ? 'لا توجد بيانات حساب' : 'No account data')}
        </div>
      )}

      {account && (
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, padding: '20px', borderRadius: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isRTL ? '140px 1fr' : '140px 1fr', rowGap: '12px', columnGap: '16px' }}>
            <div style={labelStyle}>{t.name || (language === 'ar' ? 'الاسم' : 'Name')}</div>
            {isEditing ? (
              <input
                type="text"
                value={formData.name}
                onChange={handleInputChange('name')}
                style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0',
                  width: '100%',
                  maxWidth: '360px',
                  fontSize: '1rem'
                }}
              />
            ) : (
              <div style={valueStyle}>{account.name || '-'}</div>
            )}

            <div style={labelStyle}>{t.phoneLabel || (language === 'ar' ? 'رقم الهاتف' : 'Phone')}</div>
            {isEditing ? (
              <div style={{ display: 'flex', gap: 8, direction: 'ltr', maxWidth: 360 }}>
                <select
                  value={phoneCountryCode}
                  onChange={e => setPhoneCountryCode(e.target.value)}
                  style={{
                    padding: '8px 6px', fontSize: '0.95rem', fontWeight: 600,
                    borderRadius: '6px', border: '1px solid #e0e0e0',
                    background: '#fff', cursor: 'pointer', flexShrink: 0, minWidth: 88
                  }}
                >
                  {COUNTRY_CODES.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  dir="ltr"
                  placeholder={language === 'ar' ? 'مثال: 501234567' : 'e.g. 501234567'}
                  value={formData.phone_number}
                  onChange={e => setFormData(prev => ({ ...prev, phone_number: e.target.value.replace(/\D/g, '') }))}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0',
                    fontSize: '1rem',
                    textAlign: 'left',
                    unicodeBidi: 'plaintext',
                  }}
                />
              </div>
            ) : (
              <div style={valueStyle}>{account.phone_number || '-'}</div>
            )}

            <div style={labelStyle}>{t.roleLabel || (language === 'ar' ? 'الدور' : 'Role')}</div>
            <div style={valueStyle}>{getRoleLabel(account.role)}</div>

            <div style={labelStyle}>{t.createdAt || (language === 'ar' ? 'تاريخ الإنشاء' : 'Created at')}</div>
            <div style={valueStyle}>{formatDate(account.created_at)}</div>

            {account.owner_id ? (
              <>
                <div style={labelStyle}>{t.ownerId || (language === 'ar' ? 'معرّف المالك' : 'Owner ID')}</div>
                <div style={valueStyle}>{account.owner_id}</div>
              </>
            ) : null}
          </div>
          {/* Change password — any logged-in user (including employees) */}
          <div
            style={{
              marginTop: '20px',
              paddingTop: '18px',
              borderTop: `1px solid ${colors.border}`,
            }}
          >
            <h3 style={{ margin: '0 0 10px 0', color: colors.text, fontSize: '1.05rem' }}>
              {language === 'ar' ? 'تغيير كلمة المرور' : 'Change password'}
            </h3>
            <div
              style={{
                marginBottom: '14px',
                padding: '12px 14px',
                borderRadius: '10px',
                background: '#fff7ed',
                border: `1px solid ${colors.border}`,
                fontSize: '0.86rem',
                lineHeight: 1.5,
                color: colors.text,
              }}
            >
              <strong style={{ display: 'block', marginBottom: '6px', color: colors.primaryDark }}>
                {language === 'ar' ? 'سياسة كلمة المرور في Real' : 'Real password policy'}
              </strong>
              {language === 'ar' ? (
                <>
                  الحد الأدنى <strong>6</strong> أحرف. يُنصح بكلمة أطول وتتضمن أحرفاً وأرقاماً ورموزاً، وتجنّب العبارات الواضحة أو المعلومات الشخصية.
                </>
              ) : (
                <>
                  Minimum <strong>6</strong> characters. Prefer a longer mix of letters, numbers, and symbols; avoid obvious words or personal info.
                </>
              )}
            </div>
            <p style={{ margin: '0 0 12px 0', color: colors.textMuted, fontSize: '0.88rem', lineHeight: 1.45 }}>
              {language === 'ar'
                ? 'اترك الحقول فارغة إن لم تكن تغيّر كلمة المرور الآن.'
                : 'Leave fields blank if you are not changing your password now.'}
            </p>
            <div style={{ display: 'grid', gap: '10px', maxWidth: '400px' }}>
              <input
                type="password"
                autoComplete="current-password"
                placeholder={language === 'ar' ? 'كلمة المرور الحالية' : 'Current password'}
                value={passwordForm.current}
                onChange={(e) => setPasswordForm((p) => ({ ...p, current: e.target.value }))}
                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '1rem' }}
              />
              <input
                type="password"
                autoComplete="new-password"
                placeholder={language === 'ar' ? 'كلمة المرور الجديدة (6 أحرف على الأقل)' : 'New password (min 6 characters)'}
                value={passwordForm.next}
                onChange={(e) => setPasswordForm((p) => ({ ...p, next: e.target.value }))}
                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '1rem' }}
              />
              <input
                type="password"
                autoComplete="new-password"
                placeholder={language === 'ar' ? 'تأكيد كلمة المرور الجديدة' : 'Confirm new password'}
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm((p) => ({ ...p, confirm: e.target.value }))}
                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '1rem' }}
              />
            </div>
            {!isEditing && !!(passwordForm.next && passwordForm.next.trim()) && (
              <div style={{ marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={handlePasswordSave}
                  disabled={saving}
                  style={{
                    background: colors.primary,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving
                    ? language === 'ar'
                      ? 'جاري الحفظ...'
                      : 'Saving...'
                    : language === 'ar'
                      ? 'حفظ كلمة المرور'
                      : 'Save password'}
                </button>
              </div>
            )}
          </div>

          <div style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {!isEditing ? (
              <button
                onClick={() => {
                  setIsEditing(true);
                  setError('');
                  setSuccess('');
                }}
                style={{
                  background: colors.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer'
                }}
              >
                {t.edit || (language === 'ar' ? 'تعديل' : 'Edit')}
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    background: colors.primary,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  {saving ? (t.saving || (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')) : (t.save || (language === 'ar' ? 'حفظ' : 'Save'))}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  style={{
                    background: '#fff',
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    padding: '8px 16px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  {t.cancel || (language === 'ar' ? 'إلغاء' : 'Cancel')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountInfo;
